import { IAngularEvent, IPromise, IQService, IScope, ITimeoutService } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { StateService } from "@uirouter/angularjs";
import { HttpErrorResponse } from "@angular/common/http";
import { Observable, Subscription, first, firstValueFrom, take, takeUntil } from "rxjs";
import { v4 as uuidv4 } from "uuid";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { IAggregatedReaction, IAggregatedReactionsMap, IComment, IReaction } from "@gtmhub/comments";
import { CommentService } from "@gtmhub/comments/services/comment.service";
import { ReactionService } from "@gtmhub/comments/services/reaction.service";
import { ProgressColoring } from "@gtmhub/configuration/services/progress-coloring";
import { IStateInit } from "@gtmhub/core/routing";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { CustomFieldActions } from "@gtmhub/customFields/redux/custom-field-actions";
import { ICustomFieldsStoreState } from "@gtmhub/customFields/redux/custom-field-reducer";
import dayjs from "@gtmhub/dayjs";
import { IIndicator, IUIError, UIErrorHandlingService, customNotFoundError } from "@gtmhub/error-handling";
import { IMetricMethodologySettingsUI, OkrMethodologyFacade } from "@gtmhub/goals/components/facades/okr-methodology.facade";
import { IGoal, QuantivePlusSuggestions } from "@gtmhub/goals/models";
import { GoalModalsService } from "@gtmhub/goals/services/goal-modals.service";
import { GoalService } from "@gtmhub/goals/services/goal.service";
import { getParentState } from "@gtmhub/goals/utils";
import { IInsightSelectorData } from "@gtmhub/insights";
import { localize } from "@gtmhub/localization";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { CloneOkrsModal } from "@gtmhub/okrs/components/clone-okrs/clone-okrs.component";
import { ResourceType } from "@gtmhub/okrs/components/clone-okrs/clone-or-move.types";
import { GoalsChangedEventArgs, OkrEvents } from "@gtmhub/okrs/events";
import { DisableMetricSyncModal } from "@gtmhub/okrs/metrics/components/disable-metric-sync/disable-metric-sync.modal.component";
import { MetricService } from "@gtmhub/okrs/metrics/services/metric.service";
import { metricFormStrings } from "@gtmhub/okrs/metrics/utils";
import { PlanningSessionsActions } from "@gtmhub/sessions/redux/session-actions";
import { ISessionsStoreState } from "@gtmhub/sessions/redux/session-reducer";
import { copyToClipboard } from "@gtmhub/shared/dom";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { Ng1DestroyService } from "@gtmhub/shared/services";
import { Ng1ConfirmDeleteService } from "@gtmhub/shared/services/ng1-confirm-delete.service";
import { getCopyURLNotificationEl } from "@gtmhub/shared/utils/dom-utils";
import { INgRedux } from "@gtmhub/state-management";
import { TaskService } from "@gtmhub/tasks/services/task.service";
import { ITeamsStoreState, TeamsActions } from "@gtmhub/teams";
import { getCurrentUserId } from "@gtmhub/users";
import { IPermissionsStoreState, hasPermissions } from "@gtmhub/users/redux";
import { emptyDateUTC } from "@gtmhub/util";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { ICollection } from "@webapp/core/core.models";
import { CfMap, ICustomField } from "@webapp/custom-fields/models/custom-fields.models";
import { areCustomFieldsChanged } from "@webapp/custom-fields/utils";
import { filterCustomFieldsBySession } from "@webapp/custom-fields/utils/custom-fields-filter-util";
import { FeatureFlag } from "@webapp/feature-toggles/models/feature-toggles.models";
import { FeatureTogglesFacade } from "@webapp/feature-toggles/services/feature-toggles-facade.service";
import { CloneMoveOkrModalService } from "@webapp/okrs/components/clone-move-okrs/clone-move-okr-modal.service";
import { WorkflowStatus } from "@webapp/okrs/goals/models/goal-workflow.models";
import { ICheckin, IMetricSnapshot, Metric, MetricMilestoneEventType } from "@webapp/okrs/metrics/models/metric.models";
import { UpdateMetricModalService } from "@webapp/okrs/metrics/services/update-metric-modal.service";
import { generateInsightSelectorData, isFieldPartOfWorkflowUtil } from "@webapp/okrs/utils/metric.utils";
import { isOkrAbandoned } from "@webapp/okrs/utils/utils";
import {
  QuantivePlusKeyResultsEffectiveness,
  QuantivePlusKeyResultsEffectivenessPayload,
  SuggestedEffectivenessTitle,
} from "@webapp/platform-intelligence/expandable-suggestions/shared/models/quantive-plus-effectiveness.models";
import {
  QuantivePlusKeyResultsExplanationPayload,
  QunativePlusExplanation,
} from "@webapp/platform-intelligence/expandable-suggestions/shared/models/quantive-plus-explanation.models";
import { QuantivePlusEffectivenessFacade } from "@webapp/platform-intelligence/expandable-suggestions/shared/services/quantive-plus-effectiveness/quantive-plus-effectiveness-facade.service";
import { QuantivePlusExplanationFacade } from "@webapp/platform-intelligence/expandable-suggestions/shared/services/quantive-plus-explanation/quantive-plus-explanation-facade.service";
import { generateQuantivePlusKeyResultsEffectivenessPayload } from "@webapp/platform-intelligence/expandable-suggestions/shared/utils/quantive-plus-effectiveness.utils";
import {
  SuggestionDrawerSectionType,
  SuggestionDrawerSuggestionGroupType,
  SuggestionDrawerTitleSuggestionParams,
} from "@webapp/platform-intelligence/expandable-suggestions/suggestion-drawer/shared/models/suggestion-drawer.models";
import { SuggestionDrawerIndicatorStatePropagator } from "@webapp/platform-intelligence/expandable-suggestions/suggestion-drawer/shared/utils/state-propagator";
import { IQuantivePlusSuggestionStatus, IQuantivePlusTask, minTasksCountNotToFetchSuggestions } from "@webapp/platform-intelligence/quantive-plus/models";
import { QuantivePlusAnotherSuggestionService } from "@webapp/platform-intelligence/quantive-plus/services/quantive-plus-another-suggestion.service";
import { generateAssignees, generateDummyTaskSuggestion, generateQuantivePlusKrTasksSuggestionsPayload } from "@webapp/platform-intelligence/quantive-plus/utils/utils";
import {
  PIStateProcessorInstanceSubType,
  PiStateProcessorInstance,
} from "@webapp/platform-intelligence/shared/components/pi-feedback-card/services/state-processor/pi-state-processor.models";
import { PiStateProcessorService } from "@webapp/platform-intelligence/shared/components/pi-feedback-card/services/state-processor/pi-state-processor.service";
import { removeAllPersistedSuggestionsFeedback } from "@webapp/platform-intelligence/shared/components/pi-feedback-card/utils/utils";
import { PIKrTasksSuggestionPayload } from "@webapp/platform-intelligence/shared/models/quantive-plus-suggestions.models";
import { PiFeatureVisibilityFacade } from "@webapp/platform-intelligence/shared/services/pi-feature-visibility-facade.service";
import { PiSuggestionSharedMetricStateService } from "@webapp/platform-intelligence/shared/services/pi-suggestion-shared-metric-state.service";
import { QuantivePlusSuggestionsFacade } from "@webapp/platform-intelligence/shared/services/quantive-plus-suggestions/quantive-plus-suggestions-facade.service";
import { getPiErrorData } from "@webapp/platform-intelligence/shared/utils/pi-errors.utils";
import { KeyResultFlowName, PiToggleButton, PiTrackingEventsEnum, PiTrackingFlowNameEnum } from "@webapp/platform-intelligence/shared/utils/pi-tracking";
import { PlanningSessionStatus, Session } from "@webapp/sessions/models/sessions.model";
import { PeopleSelectorRequest } from "@webapp/shared/components/people-selector/models/models";
import { DropdownMenuItem } from "@webapp/shared/dropdown/dropdown.models";
import { DropdownMenuItemBuilder } from "@webapp/shared/dropdown/util/dropdown-menu-item-builder";
import { TrackingEvents } from "@webapp/shared/utils/tracking";
import { Task } from "@webapp/tasks/models/tasks.models";

const bindStateToScope = (state: ICustomFieldsStoreState & IAssigneesStoreState & ISessionsStoreState & ITeamsStoreState) => {
  return {
    customFieldsCheckin: { items: state.customFields.map.metric_snapshot },
    assigneesIdMap: state.assignees.map,
    sessionIdMap: state.sessions.map,
    teams: state.teams.items,
    reduxItemsFetched: state.assignees.isFetched && state.sessions.isFetched && state.customFields.isFetched && state.teams.isFetched,
    reduxItemsError: state.assignees.error || state.sessions.error || state.customFields.error || state.teams.error,
  };
};

type MetricField = "name" | "description" | "initialValue" | "target" | "ownerId" | "ownerIds" | "settings" | "customFields";

interface INumericMap {
  [key: string]: number;
}

export interface IUpdateMetricField {
  updateField(metric: Partial<Metric>, errorField: MetricField): IPromise<Metric>;
}

export interface IMetricScope extends IUpdateMetricField, IScope, IModalScope, IExtendedRootScopeService, ReturnType<typeof bindStateToScope> {
  // to do: add ctrl specific interface/type fetched with https://gtmhub.atlassian.net/browse/GVS-10052
  isMilestoneFeatureAvailable: boolean;
  metric: Metric;
  effectivenessFlowId: string;
  insightSelector: {
    data: IInsightSelectorData;
  };
  piContextualSuggestionsEnabled: boolean;
  indicators: {
    loadingMetric?: IIndicator;
    loadingUpdate?: IIndicator;
    refreshingMetric?: IIndicator;
    doNotDisplaySnapshots?: IIndicator;
    saving?: IIndicator;
  };
  suggestionsStatus: {
    tasks?: IQuantivePlusSuggestionStatus;
  };
  goal: IGoal;
  editMode: {
    name: boolean;
    description: boolean;
    initialValue: boolean;
    target: boolean;
  };
  errorMessage: {
    [key in MetricField]: {
      state: boolean;
      error: string;
    };
  };
  planningSession: Session;
  msteams: boolean;
  isSuggestingTasksToOKRsEnabled: boolean;
  reactions: IAggregatedReactionsMap;
  contributingKRsTarget: {
    isCovered: boolean;
    totalAmount: Metric; // Metric is used so we can format the targetValue
    difference: Metric;
  };
  sourceMetric: Metric;
  contributingMetrics: Metric[];
  parentState: string; // this is used to provide metadata for tracking
  methodologySettings: IMetricMethodologySettingsUI;
  suggestions: QuantivePlusSuggestions;
  loadEdiatableCustomFields: boolean; // ideally when meric is migrated this variable shouldn't exist
  allowEdit: boolean;
  allowDelete: boolean;
  shouldDisplaySuggestionDrawer: boolean;
  isSuggestionStateIndicatorEnabled: boolean;
  isSuggestionDrawerOverviewTabEnabled: boolean;
  isSuggestionDrawerEffectivenessTabEnabled: boolean;
  drawerExplanationSection: SuggestionDrawerSectionType<{ explanation: QunativePlusExplanation; flowId: string }>;
  drawerEffectivenessSection: SuggestionDrawerSectionType<{ effectiveness: QuantivePlusKeyResultsEffectiveness }>;
  drawerTitleSection: SuggestionDrawerSectionType<{ title: SuggestionDrawerTitleSuggestionParams } & SuggestionDrawerSuggestionGroupType>;
  drawerStatePropagator: SuggestionDrawerIndicatorStatePropagator;
  peopleSelectorRequest: PeopleSelectorRequest;
  flowName: PiTrackingFlowNameEnum.KeyResultDetailsView;
  dropdownMenuItems: DropdownMenuItem[];
  tasksInstance: PiStateProcessorInstance;
  isA14UpdateKeyResult: boolean;
  isNewCloneAndMoveModalEnabled: boolean;

  trackContextualSuggestionsToggle(opts: { state: boolean; subEntity: PIStateProcessorInstanceSubType }): void;
  getSnapshotSortField(snapshot: IMetricSnapshot): number;
  redirectToGoal(sessionId: string, goalId: string);
  closeMetricForm(): void;
  closeMetricModal($event): void;
  openCloneMetricModal(metric: Metric): void;
  confirmDeleteMetric(metricId: string, goalId: string);
  openMoveMetricModal(metric: Metric): void;
  toggleEditMode(field: MetricField, state?: { state: boolean }): void;
  updateCustomFields(customFields: CfMap): void;
  updateName(): void;
  updateDescription(): void;
  updateInitialValue(): void;
  updateTarget(): void;
  openMetricInAlignmentView($event): void;
  updateMultipleOwners(ids: string[]): void;
  openDisableSyncModal(metricId: string): void;
  canUpdateMetric(): boolean;
  isMetricOfAbandonedGoal(): boolean;
  openMetricToUpdate(): void;
  copyItemUrlToClipboard(): void;
  isFieldPartOfWorkflow(filedName: string): boolean;
  onInsightSelectorChange(): void;
  onKrTaskAdded(suggestionIndex?: number, currentMetricTasksCount?: number): void;
  onKrTaskAddedMigrated(event: { suggestionIndex?: number; currentMetricTasksCount?: number; flowName?: KeyResultFlowName }): void;
  onKrTaskRemoved(currentMetricTasksCount: number): void;
  onKrTaskRemovedMigrated(event: { currentMetricTasksCount: number }): void;
  onSuggestionDrawerVisibilityChange(isVisible: boolean): void;
  filterBySessionId(customFields: ICustomField[]): ICustomField[];
  isCustomFieldDisabled(customFieldName: string): boolean;
  handleClickOnHiddenOverflowArea($event): void;
}

export interface IMetricParams {
  metricId: string;
  metricReturnState: string;
  sharedMetric?: string;
}

export class MetricCtrl implements IStateInit {
  private okrEvents: OkrEvents;
  private metricPromise: IPromise<Metric>;
  private persistedMetric: Metric;
  private metricActions: string[];
  private quantiveTasksFlowId: string;
  private persistedMetricFields: {
    description: string;
    customFields: CfMap;
  } = { description: "", customFields: {} };
  private suggestAnotherKrTaskSubscription: Subscription;
  private refreshAllKrTasksSubscription: Subscription;
  private destroy$: Observable<unknown>;

  public static $inject = [
    "$scope",
    "$rootScope",
    "$q",
    "MetricService",
    "$stateParams",
    "UIErrorHandlingService",
    "CustomFieldActions",
    "GoalService",
    "CommentService",
    "$timeout",
    "$state",
    "ReactionService",
    "GoalModalsService",
    "$ngRedux",
    "ProgressColoring",
    "AssigneeActions",
    "CloneOkrsModal",
    "DisableMetricSyncModal",
    "PlanningSessionsActions",
    "OkrMethodologyFacade",
    "TaskService",
    "TeamsActions",
    "QuantivePlusSuggestionsFacade",
    "QuantivePlusAnotherSuggestionService",
    "AnalyticsService",
    "PiFeatureVisibilityFacade",
    "QuantivePlusExplanationFacade",
    "QuantivePlusEffectivenessFacade",
    "PiSuggestionSharedMetricStateService",
    "PiStateProcessorService",
    "Ng1ConfirmDeleteService",
    "UpdateMetricModalService",
    "FeatureTogglesFacade",
    "AccountService",
    "CloneMoveOkrModalService",
  ];

  constructor(
    private $scope: IMetricScope,
    private $rootScope: IGtmhubRootScopeService & ITraceRootScopeService,
    private $q: IQService,
    private metricService: MetricService,
    private $stateParams: IMetricParams,
    private uiErrorHandlingService: UIErrorHandlingService,
    private customFieldActions: CustomFieldActions,
    private goalService: GoalService,
    private commentService: CommentService,
    private $timeout: ITimeoutService,
    private $state: StateService,
    private reactionService: ReactionService,
    private goalModalsService: GoalModalsService,
    private $ngRedux: INgRedux,
    private progressColoring: ProgressColoring,
    private assigneeActions: AssigneeActions,
    private cloneOkrsModal: CloneOkrsModal,
    private disableMetricSync: DisableMetricSyncModal,
    private planningSessionsActions: PlanningSessionsActions,
    private okrMethodologyFacade: OkrMethodologyFacade,
    private taskService: TaskService,
    private teamsActions: TeamsActions,
    private quantivePlusSuggestionsFacade: QuantivePlusSuggestionsFacade,
    private quantivePlusAnotherSuggestionService: QuantivePlusAnotherSuggestionService,
    private analyticsService: AnalyticsService,
    private piFeatureVisibilityFacade: PiFeatureVisibilityFacade,
    private quantivePlusExplanationFacade: QuantivePlusExplanationFacade,
    private quantivePlusEffectivenessFacade: QuantivePlusEffectivenessFacade,
    private piSuggestionSharedMetricStateService: PiSuggestionSharedMetricStateService,
    private piStateProcessorService: PiStateProcessorService,
    private confirmDeleteService: Ng1ConfirmDeleteService,
    private updateMetricModalService: UpdateMetricModalService,
    private featureToggleService: FeatureTogglesFacade,
    private accountService: AccountService,
    private cloneMoveModalService: CloneMoveOkrModalService
  ) {
    this.metricActions = [];
    this.$scope.allowEdit = false;
    this.$scope.allowDelete = false;
    this.okrEvents = new OkrEvents(this.$scope);
    this.$scope.loadEdiatableCustomFields = false;
    this.$scope.handleClickOnHiddenOverflowArea = this.handleClickOnHiddenOverflowArea;
    this.$scope.dropdownMenuItems = [];

    this.$scope.indicators = {
      doNotDisplaySnapshots: { progress: true },
    };
    this.$scope.contributingMetrics = [];

    // if use open insight from metric details and press back button
    if (this.$rootScope.noNavigation) {
      this.$rootScope.noNavigation = false;
    }
    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);

    this.okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));

    this.$scope.$on("commentPosted", this.refreshMetric);
    this.$scope.$on("commentDeleted", this.refreshMetric);
    const removeReactionCreatedListener = this.$rootScope.$on("reactionCreated", this.onReactionCreated);
    const removeReactionDeletedListener = this.$rootScope.$on("reactionDeleted", this.onReactionDeleted);
    this.$scope.$on("$destroy", () => {
      unsubscribe();
      removeReactionCreatedListener();
      removeReactionDeletedListener();
      this.suggestAnotherKrTaskSubscription?.unsubscribe();
      this.refreshAllKrTasksSubscription?.unsubscribe();
    });

    this.$scope.getSnapshotSortField = this.getSnapshotSortField;
    this.$scope.redirectToGoal = this.redirectToGoal;
    this.$scope.closeMetricForm = this.closeMetricForm;
    this.$scope.closeMetricModal = this.closeMetricModal;
    this.$scope.confirmDeleteMetric = this.confirmDeleteMetric;
    this.$scope.openCloneMetricModal = this.openCloneMetricModal;
    this.$scope.openMoveMetricModal = this.openMoveMetricModal;
    this.$scope.toggleEditMode = this.toggleEditMode;
    this.$scope.updateCustomFields = this.updateCustomFields;
    this.$scope.updateField = this.updateField;
    this.$scope.updateDescription = this.updateDescription;
    this.$scope.updateName = this.updateName;
    this.$scope.updateInitialValue = this.updateInitialValue;
    this.$scope.updateTarget = this.updateTarget;
    this.$scope.updateMultipleOwners = this.updateMultipleOwners;
    this.$scope.openDisableSyncModal = this.openDisableSyncModal;
    this.$scope.canUpdateMetric = this.canUpdateMetric;
    this.$scope.isMetricOfAbandonedGoal = this.isMetricOfAbandonedGoal;
    this.$scope.openMetricToUpdate = this.openMetricToUpdate;
    this.$scope.copyItemUrlToClipboard = this.copyItemUrlToClipboard;
    this.$scope.isFieldPartOfWorkflow = (fieldName) => this.isFieldPartOfWorkflow(fieldName);
    this.$scope.onInsightSelectorChange = this.onInsightSelectorChange;
    this.$scope.onKrTaskAdded = this.onKrTaskAdded;
    this.$scope.onKrTaskAddedMigrated = this.onKrTaskAddedMigrated;
    this.$scope.onKrTaskRemoved = this.onKrTaskRemoved;
    this.$scope.onKrTaskRemovedMigrated = this.onKrTaskRemovedMigrated;
    this.$scope.onSuggestionDrawerVisibilityChange = this.changeSuggestionDrawerVisibility;
    this.$scope.filterBySessionId = this.filterBySessionId;
    this.$scope.isCustomFieldDisabled = this.isCustomFieldDisabled;
    this.$scope.openMetricInAlignmentView = this.openMetricInAlignmentView;

    this.setSuggestionDrawerInitialValues();

    this.$scope.errorMessage = {
      description: {
        state: false,
        error: null,
      },
      name: {
        state: false,
        error: null,
      },
      initialValue: {
        state: false,
        error: null,
      },
      target: {
        state: false,
        error: null,
      },
      ownerId: {
        state: false,
        error: null,
      },
      ownerIds: {
        state: false,
        error: null,
      },
      settings: {
        state: false,
        error: null,
      },
      customFields: {
        state: false,
        error: null,
      },
    };

    this.destroy$ = new Ng1DestroyService(this.$scope);

    this.featureToggleService
      .isFeatureAvailable$(FeatureFlag.PiContextualSuggestions)
      .pipe(take(1))
      .subscribe({
        next: (enabled) => {
          this.$scope.piContextualSuggestionsEnabled = enabled;
        },
      });

    this.$scope.trackContextualSuggestionsToggle = this.trackContextualSuggestionsToggle;
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private openUpdateMetricModal(): void {
    this.updateMetricModalService
      .openUpdateMetricModal({
        metricId: this.$scope.metric.id,
      })
      .pipe(take(1))
      .subscribe();
  }

  private trackContextualSuggestionsToggle = (opts: { state: boolean; subEntity: PIStateProcessorInstanceSubType }): void => {
    this.analyticsService.track(TrackingEvents.ToggleSet, {
      name: PiToggleButton,
      state: opts.state,
      entity: "key_result",
      subEntity: opts.subEntity,
    });
  };

  private setPIInstances(): void {
    this.$scope.tasksInstance = this.piStateProcessorService.createNewInstance({
      type: "metric",
      subType: "task",
      isInline: true,
      improveSuggestionContext: {
        entityId: this.$scope.metric.id,
        parentId: this.$scope.metric.goalId,
        parentType: "objective",
      },
    });

    this.$scope.tasksInstance.subject.pipe(takeUntil(this.destroy$)).subscribe((data) => {
      if (data.removeSuggestion) {
        this.onQuantivePlusTaskRejected(data.suggestion.index);
      }
    });
  }

  private onQuantivePlusTaskRejected = (index: number): void => {
    const task = this.$scope.suggestions.tasks.at(index);
    if (!task) {
      return;
    }

    task.isHidden = true;
    this.$scope.$evalAsync();
  };

  private setSuggestionDrawerInitialValues() {
    this.$scope.drawerExplanationSection = {
      isLoading: false,
      hasError: false,
      errorData: getPiErrorData("explanation", null),
      explanation: undefined,
      flowId: undefined,
      onRetry: () => this.loadSuggestionDrawerExplanation({ ...this.$scope.metric }),
    };
    this.$scope.drawerEffectivenessSection = {
      isLoading: false,
      hasError: false,
      errorData: getPiErrorData("effectiveness", null),
      effectiveness: undefined,
      onRetry: () => this.loadSuggestionDrawerEffectiveness({ ...this.$scope.metric }),
    };
    this.$scope.drawerTitleSection = {
      isLoading: false,
      hasError: false,
      isHidden: false,
      errorData: getPiErrorData("title", null),
      hasAllDependencies: true,
      noSuggestionReason: "",
      suggestions: [{ id: "", suggestionText: "" }],
      title: {
        isTitleAvailable: false,
        isSessionAvailable: false,
        isOwnerAvailable: false,
      },
      improveSuggestionContext: {
        parentId: "",
        parentType: "",
        entityId: "",
      },
      onAcceptSuggestion: () => this.setAcceptedTitleSuggestion(),
      onSuggestAnother: () =>
        this.loadTitleSuggestionPartOfEffectiveness({
          ...this.$scope.metric,
          isRefresh: true,
          rejected: { effectiveness: [{ title: this.$scope.drawerTitleSection.suggestions[0].suggestionText }] },
        }),
      onRetry: () => this.loadTitleSuggestionPartOfEffectiveness({ ...this.$scope.metric, isRefresh: false, rejected: {} }),
      goBackToLastAvailableSuggestion: () => this.showLastAvailableTitleSuggestion(),
    };
    this.$scope.drawerStatePropagator = new SuggestionDrawerIndicatorStatePropagator();
  }

  private setSuggestionDrawerSectionsImproveSuggestionContext = (): void => {
    this.$scope.drawerTitleSection = {
      ...this.$scope.drawerTitleSection,
      improveSuggestionContext: {
        entityId: this.$scope.metric.id,
        parentId: this.$scope.metric.goalId,
        parentType: "objective",
      },
    };
  };

  private isFieldPartOfWorkflow(fieldName: string): boolean {
    return isFieldPartOfWorkflowUtil(fieldName, this.$scope.planningSession);
  }

  canUpdateMetric = (): boolean => {
    return this.$rootScope.hasPermission("ManageGoals") && !this.$scope.metric.sourceMetricId && !this.isMetricOfAbandonedGoal() && this.metricActions.includes("update");
  };

  isMetricOfAbandonedGoal = (): boolean => {
    return isOkrAbandoned(this.$scope.metric.goalClosedStatus);
  };

  openMetricToUpdate = (): void => {
    if (this.canUpdateMetric()) {
      if (this.$scope.isA14UpdateKeyResult) {
        this.openUpdateMetricModal();
      } else {
        this.$state.go("._updateMetric.update", { metricId: this.$scope.metric.id });
      }
    }
  };

  private strings = {
    msTeamsHomeState: "gtmhub.msTeamsHome",
    comments: "comments",
    reactions: "reactions",
    double: "double",
    boolean: "boolean",
    empty: "",
    at_least: "at_least",
    at_most: "at_most",
    should_stay_above: "should_stay_above",
    should_stay_below: "should_stay_below",
  };

  private closeMetricForm = (): void => {
    this.$state.go(this.strings.msTeamsHomeState);
  };

  private handleClickOnHiddenOverflowArea = ($event: PointerEvent): void => {
    const eventTarget = $event.target as HTMLElement;

    if (eventTarget.classList.contains("scrollable-with-left-overflow")) {
      this.$scope.$dismiss();
    }
  };

  private closeMetricModal = ($event): void => {
    if (!$event || $event.keyCode === 13) {
      this.$scope.$dismiss();
    }
  };

  private checkA14CloneAndMoveModalsFeatureToggle = (): IPromise<void> => {
    return this.featureToggleService.isFeatureAvailable(FeatureFlag.CloneAndMoveOkrModal).then((isNewCloneAndMoveModalEnabled) => {
      this.$scope.isNewCloneAndMoveModalEnabled = isNewCloneAndMoveModalEnabled;
    });
  };

  private load = (): IPromise<unknown> => {
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());
    this.$ngRedux.dispatch(this.customFieldActions.getCustomFields());
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());

    return this.$q
      .all([
        this.checkSuggestionStateIndicatorFeatureToggle(),
        this.checkSuggestionDrawerOverviewTabFeatureToggle(),
        this.checkSuggestionDrawerEffectivenessTabFeatureToggle(),
        this.checkA14CloneAndMoveModalsFeatureToggle(),
        this.$scope.watchUntilAndRejectOnError(
          () => this.$scope.reduxItemsFetched,
          () => this.$scope.reduxItemsError
        ),
        firstValueFrom(this.featureToggleService.isFeatureAvailable$(FeatureFlag.MetricMilestone)).then((isEnabled) => {
          this.$scope.isMilestoneFeatureAvailable = isEnabled;
        }),
      ])
      .then(() => {
        return this.getMetric(this.$stateParams.metricId).then(
          (metric) => {
            this.metricActions = metric.currentUserAllowedActions;

            if (!metric.goalId && metric.obfuscated) {
              const error = {
                type: "private_okr",
                title: localize("this_okr_is_private"),
              };
              if (this.$scope.indicators.loadingUpdate) {
                this.$scope.indicators.loadingUpdate = { error };
              } else {
                this.$scope.indicators.loadingMetric = { error };
              }
            } else {
              delete this.$scope.indicators.loadingUpdate;
              delete this.$scope.indicators.loadingMetric;
              this.$scope.metric = metric;
              this.persistedMetric = angular.copy(metric);

              this.okrEvents.broadcastGoalsChanged("metricVisited", { goalId: this.$scope.metric.goalId, metric: this.$scope.metric });
              this.processMetric(metric);
              this.processContributingKRs(metric);
              this.getSourceMetric(metric.sourceMetricId);
              this.getContributingMetricsNames(metric);
              this.syncGoalStoredFields();
              this.setPIInstances();
              this.setSuggestionDrawerSectionsImproveSuggestionContext();
              return this.getGoal(metric.goalId);
            }

            this.$scope.insightSelector = { data: generateInsightSelectorData(metric) };
          },
          (error: IUIError) => {
            if (this.$scope.indicators.loadingUpdate) {
              this.$scope.indicators.loadingUpdate = { error };
            } else {
              this.$scope.indicators.loadingMetric = { error };
            }
          }
        );
      });
  };

  private setPeopleSelectorRequest(): void {
    this.$scope.peopleSelectorRequest = {
      ...new PeopleSelectorRequest(),
      type: this.$scope.methodologySettings.assignMetricPermissions,
      permissionSetting: { enabled: true, permission: "ManageGoals" },
    };
  }

  private init = (): IPromise<unknown> => {
    this.setSuggestionListeners();
    this.addReturnStateForSharedUrl();

    this.$scope.indicators.loadingMetric = { progress: true };
    this.$scope.indicators.doNotDisplaySnapshots = { progress: true };
    this.$scope.flowName = PiTrackingFlowNameEnum.KeyResultDetailsView;
    this.$scope.editMode = {
      name: false,
      description: false,
      initialValue: false,
      target: false,
    };
    this.$scope.parentState = getParentState(this.$state.current.name);
    this.$scope.suggestionsStatus = {};

    this.featureToggleService.isFeatureAvailable(FeatureFlag.A14UpdateKeyResult).then((isA14UpdateKeyResult) => {
      this.$scope.isA14UpdateKeyResult = isA14UpdateKeyResult;
    });

    return this.load().then(() => {
      this.onGoalAndMetricLoaded();
    });
  };

  private onGoalAndMetricLoaded = (): void => {
    if (this.$scope.metric && this.$rootScope.hasPermission("ManageGoals")) {
      this.$scope.dropdownMenuItems = this.createDropdownOptions();
    }
    this.loadSuggestionDrawerEffectiveness({ ...this.$scope.metric });
  };

  private addReturnStateForSharedUrl = (): void => {
    if (this.$stateParams.sharedMetric) {
      // the returnState is passed to _metric() abstract state which uses the modal util method in routing
      const params = { ...this.$stateParams, sharedMetric: null, returnState: "^.^" };
      this.$state.go(".", params, { notify: false });
    }
  };

  private onInsightSelectorChange = (): void => {
    const shouldPredict = {
      format: true,
      targetOperator: true,
      target: true,
      initialValue: true,
    };

    this.$scope.metric = this.metricService.updateMetricInsightData(this.$scope.metric, this.$scope.insightSelector.data, shouldPredict);

    this.$scope.indicators.saving = { progress: true };
    this.metricService.patchMetric(this.$scope.metric).then(
      () => {
        delete this.$scope.indicators.saving;

        this.okrEvents.broadcastGoalsChanged("metricUpdated", {
          metric: this.$scope.metric,
          goal: this.$scope.goal,
          session: this.$scope.sessionIdMap[this.$scope.goal.sessionId],
        });
        this.okrEvents.broadcastGoalsChanged("goalChanged");
        this.$rootScope.$broadcast("goalsChanged");
      },
      (error) => (this.$scope.indicators.saving = { error })
    );
  };

  private onGoalsChanged = (args: GoalsChangedEventArgs): void => {
    switch (args.reason) {
      case "metricMoved":
        this.onMetricMoved(args.detail);
        break;
      case "metricPatched":
        if (!args.detail.patch) {
          break;
        }
        if (
          args.detail.patch.initialValue !== undefined ||
          args.detail.patch.target !== undefined ||
          args.detail.patch.targetOperator !== undefined ||
          args.detail.patch.actual !== undefined
        ) {
          this.refresh();
        }
        break;
      case "metricUpdated":
        this.refresh();
        break;
      case "metricCommentEdit":
        this.refresh();
        break;
      case "metricSnapshotDeleted":
        this.refresh();
        break;
      case "metricChanged":
        this.refresh();
        break;
    }
  };

  private syncGoalStoredFields(): void {
    this.persistedMetricFields.description = this.$scope.metric.description;
    this.persistedMetricFields.customFields = { ...this.$scope.metric.customFields };
  }

  private updateDescription = (): void => {
    /** needs explicit check since "" is a valid description, while undefined or null is only returned by API */
    if (typeof this.$scope.metric.description === "undefined" || this.$scope.metric.description === null) {
      return;
    }

    if (this.persistedMetricFields.description !== this.$scope.metric.description) {
      this.updateField({ description: this.$scope.metric.description }, "description").then(() => {
        this.syncGoalStoredFields();
        this.reflectFieldChangeWithinSuggestionDrawer({ ...this.$scope.metric, description: this.$scope.metric.description });
      });
    }
  };

  private updateCustomFields = (customFields: CfMap): void => {
    if (areCustomFieldsChanged(customFields, this.persistedMetricFields.customFields)) {
      const partialMetric = { customFields: customFields };
      this.updateField(partialMetric, "customFields").then(() => this.syncGoalStoredFields());
    }
  };

  private updateField = (update: Partial<Metric>, updatedField: MetricField): IPromise<Metric> => {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    if (this.metricPromise) {
      const updatePromise: IPromise<{ metric: Metric; error: IUIError }> = this.metricPromise.then((metric) =>
        this.patch(update, updatedField)
          .then((updatedMetric) => ({ metric: updatedMetric, error: null }))
          .catch((error) => ({ metric, error }))
      );
      this.metricPromise = updatePromise.then(({ metric }) => metric);

      return updatePromise.then(({ metric, error }) => {
        this.$scope.errorMessage[updatedField].state = false;
        if (error) {
          this.$scope.errorMessage[updatedField].state = true;
          return this.$q.reject(error);
        }

        return metric;
      });
    } else {
      this.metricPromise = this.patch(update, updatedField);
    }

    removeAllPersistedSuggestionsFeedback("metric", this.$scope.metric.id);

    return this.metricPromise;
  };

  private patch = (partialMetric: Partial<Metric>, updatedField: MetricField): IPromise<Metric> => {
    return this.$rootScope.traceAction(`update_KR_fields-${updatedField}`, () => {
      partialMetric.id = this.$scope.metric.id;
      this.$scope.errorMessage[updatedField].state = false;

      // GVS-19943 call checkin if the metric is dynamic
      const shouldCheckin =
        partialMetric.initialValue !== undefined &&
        partialMetric.initialValue !== this.$scope.metric.actual &&
        !this.$scope.metric.metricHistory?.length &&
        this.$scope.metric.insightName;

      // GVS-19943
      const shouldUpdateActual =
        !this.$scope.metric.insightName &&
        (this.$scope.metric.manualType === metricFormStrings.double || this.$scope.metric.manualType === metricFormStrings.empty) &&
        this.$scope.metric.metricHistory?.length === 0;

      if (shouldUpdateActual && partialMetric.initialValue && partialMetric.initialValue !== this.$scope.metric.actual) {
        this.$scope.metric.actual = partialMetric.initialValue;
        partialMetric.actual = partialMetric.initialValue;
      }

      const patch = (partialMetric: Partial<Metric>): IPromise<Metric> => {
        return this.metricService.patchMetric(partialMetric).then(
          (updatedMetric) => {
            this.okrEvents.broadcastGoalsChanged("goalChanged");
            this.$rootScope.$broadcast("goalsChanged");
            Object.assign(this.persistedMetric, partialMetric);
            this.$scope.errorMessage[updatedField].state = false;

            return updatedMetric;
          },
          (error) => {
            this.metricPromise = null;
            this.$scope.errorMessage[updatedField].state = true;
            return error;
          }
        );
      };

      if (shouldCheckin) {
        const checkin: ICheckin = {
          checkInDate: new Date().toISOString(),
          actual: this.$scope.metric.actual,
          confidence: this.$scope.metric.confidence?.value,
          customFields: this.$scope.metric.customFields,
        };
        return this.metricService.checkin({ id: this.$scope.metric.id }, checkin).then(() => {
          this.okrEvents.broadcastGoalsChanged("metricSnapshotCreated", {
            checkin,
            metric: this.$scope.metric,
            goal: this.$scope.goal,
            session: this.$scope.planningSession,
          });
          return patch(partialMetric);
        });
      } else {
        return patch(partialMetric);
      }
    });
  };

  private toggleEditMode = (field: MetricField, { state } = { state: null }): void => {
    if (!this.$scope.allowEdit || (this.$scope.planningSession?.workflow && this.$scope.goal?.workflow?.status === "approved" && !this.isFieldPartOfWorkflow(field))) {
      return;
    }

    this.$scope.editMode[field] = state !== false;

    window.setTimeoutOutsideAngular(() => {
      const fieldEl = document.getElementById(`single-metric-${field}`);
      if (fieldEl) {
        fieldEl.focus();
      }
    });
  };

  private updateName = (): void => {
    if (this.$scope.metric.name === this.persistedMetric.name) {
      return;
    }
    this.updateField({ name: this.$scope.metric.name }, "name").then(() => {
      this.reflectFieldChangeWithinSuggestionDrawer({ ...this.$scope.metric, name: this.$scope.metric.name });
    });
  };

  private updateMultipleOwners = (ids: string[]): void => {
    if (ids.length === 0) {
      return;
    }

    if (this.$scope.metric.ownerIds.toString() === ids.toString()) {
      return;
    }

    this.$scope.metric.ownerIds = ids;
    this.updateField({ ownerIds: ids }, "ownerIds").then(() => {
      this.reflectFieldChangeWithinSuggestionDrawer({ ...this.$scope.metric, ownerIds: ids });
    });
  };

  private updateInitialValue = (): void => {
    this.$scope.metric.initialValue = typeof this.$scope.metric.initialValue === "undefined" ? this.persistedMetric.initialValue : this.$scope.metric.initialValue;
    this.validateFieldValue("initialValue");
    if (this.$scope.errorMessage.initialValue.state || this.$scope.metric.initialValue === this.persistedMetric.initialValue) {
      return;
    }
    this.updateField({ initialValue: Number(this.$scope.metric.initialValue) }, "initialValue");
  };

  private updateTarget = (): void => {
    this.$scope.metric.target = typeof this.$scope.metric.target === "undefined" ? this.persistedMetric.target : this.$scope.metric.target;
    this.validateFieldValue("target");
    if (this.$scope.errorMessage.target.state || this.$scope.metric.target === this.persistedMetric.target) {
      return;
    }
    this.updateField({ target: Number(this.$scope.metric.target) }, "target").then(() => {
      this.loadSuggestionDrawerEffectiveness({ ...this.$scope.metric, target: Number(this.$scope.metric.target) });
    });
  };

  private validateFieldValue = (field: MetricField): void => {
    this.$scope.errorMessage[field].error = this.validateDoubleInputValues(
      this.persistedMetric.manualType,
      this.persistedMetric.targetOperator,
      field === "initialValue" ? this.$scope.metric.initialValue : this.persistedMetric.initialValue,
      field === "target" ? this.$scope.metric.target : this.persistedMetric.target,
      this.persistedMetric.critical
    );
    if (this.$scope.errorMessage[field].error) {
      this.$scope.errorMessage[field].state = true;
    } else {
      this.$scope.errorMessage[field].state = false;
    }
  };

  private validateDoubleInputValues = (manualType: string, targetOperator: string, initialValue: number, targetValue: number, criticalValue: number): string => {
    const isManualTypeDoubleOrEmpty = manualType === this.strings.double || manualType === this.strings.empty;

    if (isManualTypeDoubleOrEmpty && targetOperator === this.strings.at_least && initialValue >= targetValue) {
      return "initial_lower_than_target";
    }

    if (isManualTypeDoubleOrEmpty && targetOperator === this.strings.at_most && initialValue <= targetValue) {
      return "initial_greater_than_target";
    }

    if (
      isManualTypeDoubleOrEmpty &&
      targetOperator === this.strings.should_stay_above &&
      criticalValue !== null &&
      typeof criticalValue !== "undefined" &&
      criticalValue >= targetValue
    ) {
      return "critical_lower_than_target";
    }

    if (
      isManualTypeDoubleOrEmpty &&
      targetOperator === this.strings.should_stay_below &&
      criticalValue !== null &&
      typeof criticalValue !== "undefined" &&
      criticalValue <= targetValue
    ) {
      return "critical_greater_than_target";
    }

    return null;
  };

  private openMoveMetricModal = (metric: Metric): void => {
    if (this.$scope.isNewCloneAndMoveModalEnabled) {
      this.cloneMoveModalService.openMoveOkrModal(ResourceType.KeyResult, this.$scope.goal, metric);
    } else {
      this.goalModalsService.openModalForMove("metric", metric, this.$scope.goal);
    }
  };

  private openCloneMetricModal = (metric: Metric): void => {
    if (this.$scope.isNewCloneAndMoveModalEnabled) {
      this.cloneMoveModalService.openCloneKrModal(metric);
    } else {
      this.cloneOkrsModal.openForMetricClone(metric);
    }
  };

  private confirmDeleteMetric = (metricId: string, goalId: string) => {
    this.confirmDeleteService.confirmDeleteItem({ itemKey: "key_result" }).subscribe(() => {
      this.$rootScope.traceAction("delete_KR", () => {
        this.metricService.deleteMetric(goalId, metricId).then(() => {
          this.trackMetricMilestoneDelete();
          this.okrEvents.broadcastGoalsChanged("metricDeleted", {
            goalId: this.$scope.goal.id,
            metricId: this.$scope.metric.id,
          });

          this.okrEvents.broadcastGoalsChanged("goalChanged");
          this.$rootScope.$broadcast("goalsChanged");

          this.$scope.$dismiss();
        });
      });
    });
  };

  private trackMetricMilestoneDelete = (): void => {
    this.featureToggleService
      .isFeatureAvailable$(FeatureFlag.MetricMilestone)
      .pipe(first())
      .subscribe((isFeatureEnabled) => {
        if (isFeatureEnabled) {
          if (!this.$scope.metric) {
            return;
          }

          this.metricService.trackMetricMilestone(MetricMilestoneEventType.KR_MILESTONES_DELETED, { metric: this.$scope.metric });
        }
      });
  };

  private measureDueDateDifference = (metric: Metric) => {
    if (metric.dueDate && metric.dueDate !== emptyDateUTC) {
      metric.daysLeft = dayjs(metric.dueDate).diff(dayjs()) / 86400000;
    }

    if (metric.dueDate && metric.softDueDate !== emptyDateUTC) {
      metric.daysLeftToSoftDeadline = dayjs(metric.softDueDate).diff(dayjs()) / 86400000;
    }
  };

  private initAllowedActions = (): void => {
    this.$scope.allowEdit =
      this.$rootScope.hasPermission("ManageGoals") &&
      this.metricActions.includes("update") &&
      this.$scope.planningSession &&
      this.$scope.planningSession.status === PlanningSessionStatus.OPEN &&
      (!this.$scope.goal.locked || this.sessionWorkflowHasMetricFields());

    this.$scope.allowDelete =
      this.$rootScope.hasPermission("ManageGoals") &&
      this.metricActions.includes("delete") &&
      this.$scope.planningSession &&
      this.$scope.planningSession.status === PlanningSessionStatus.OPEN &&
      !this.$scope.goal.locked &&
      !this.isMetricOfAbandonedGoal();

    this.$scope.loadEdiatableCustomFields = true;
  };

  private sessionWorkflowHasMetricFields(): boolean {
    return this.$scope.planningSession.workflow?.editableKrFields?.length > 0;
  }

  private onMetricMoved = ({ goalId, metric }: { goalId: string; metric: Metric }) => {
    this.$scope.metric = metric;

    this.processMetric(this.$scope.metric);
    this.getGoal(goalId);
  };

  private onKrTaskAdded = (suggestionIndex?: number, currentMetricTasksCount?: number, flowName?: KeyResultFlowName): void => {
    if (this.$scope.suggestions) {
      if (suggestionIndex || suggestionIndex === 0) {
        this.$scope.suggestions.tasks.splice(suggestionIndex, 1);

        this.sendQuantivePlusTracking(PiTrackingEventsEnum.PiSuggestionAccepted, flowName);
      } else {
        this.$scope.suggestions.tasks.shift();
      }

      if (currentMetricTasksCount >= minTasksCountNotToFetchSuggestions) {
        this.addQuantivePlusKrTaskSuggestion();
      }
    }
  };

  private onKrTaskAddedMigrated = (event: { suggestionIndex?: number; currentMetricTasksCount?: number; flowName?: KeyResultFlowName }): void => {
    if (this.$scope.suggestions) {
      if (event.suggestionIndex || event.suggestionIndex === 0) {
        this.$scope.suggestions.tasks.splice(event.suggestionIndex, 1);

        this.sendQuantivePlusTracking(PiTrackingEventsEnum.PiSuggestionAccepted, event.flowName);
      } else {
        this.$scope.suggestions.tasks.shift();
      }

      if (event.currentMetricTasksCount >= minTasksCountNotToFetchSuggestions) {
        this.addQuantivePlusKrTaskSuggestion();
      }
    }
  };

  private onKrTaskRemoved = (currentMetricTasksCount: number): void => {
    if (this.$scope.isSuggestingTasksToOKRsEnabled && currentMetricTasksCount + this.$scope.suggestions?.tasks?.length < minTasksCountNotToFetchSuggestions) {
      this.addQuantivePlusKrTaskSuggestion();
    }
  };

  private onKrTaskRemovedMigrated = (event: { currentMetricTasksCount: number }): void => {
    if (this.$scope.isSuggestingTasksToOKRsEnabled && event.currentMetricTasksCount + this.$scope.suggestions?.tasks?.length < minTasksCountNotToFetchSuggestions) {
      this.addQuantivePlusKrTaskSuggestion();
    }
  };

  private shouldFetchQuantivePlusKrTasksSuggestions = (): boolean => {
    return hasPermissions(this.$ngRedux.getState<IPermissionsStoreState>(), "ManageTasks");
  };

  private addQuantivePlusKrTaskSuggestion = (): void => {
    if (!this.$scope.suggestions) {
      this.$scope.suggestions = { sessionId: this.$scope.goal.sessionId, tasks: [], keyresults: [] };
    }

    const dummyTaskSuggestionToShowAsLoading: IQuantivePlusTask = generateDummyTaskSuggestion({ isLoading: true });
    this.$scope.suggestions.tasks.push(dummyTaskSuggestionToShowAsLoading);

    const onError = (err: HttpErrorResponse): void => {
      const dummyTaskSuggestionToShowAsError: IQuantivePlusTask = generateDummyTaskSuggestion({
        isError: true,
        errorData: getPiErrorData("task", err),
      });

      const firstDummyLoadingTaskIndex = this.$scope.suggestions.tasks.findIndex((task) => task.isLoading);
      this.$scope.suggestions.tasks[firstDummyLoadingTaskIndex] = dummyTaskSuggestionToShowAsError;
    };

    this.getMetricAndParentGoalExistingTasksData().then(
      (existingTasks) => {
        const combinedGoalAndMetricTasks = [...existingTasks.goalTasks, ...existingTasks.metricTasks];
        const tasksToSuggest = 1;

        const payload = this.getQuantivePlusKrTasksSuggestionsPayload({
          metricAndParentGoalExistingTasks: combinedGoalAndMetricTasks,
          tasksToSuggest: tasksToSuggest,
        });

        this.quantivePlusSuggestionsFacade
          .getQuantivePlusKrTasksSuggestion(payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (suggestions) => {
              const taskSuggestion = suggestions.suggestions.tasks[0];
              const firstDummyLoadingTaskIndex = this.$scope.suggestions.tasks.findIndex((task) => task.isLoading);
              this.$scope.suggestions.tasks[firstDummyLoadingTaskIndex] = taskSuggestion;

              this.$scope.$evalAsync();
            },
            error: (err: HttpErrorResponse) => onError(err),
          });
      },
      (err: HttpErrorResponse) => onError(err)
    );
  };

  private getQuantivePlusKrTasksSuggestions = (): void => {
    this.$scope.suggestionsStatus.tasks = { isLoading: true };

    this.piSuggestionSharedMetricStateService.setGoal(this.$scope.goal);
    this.piSuggestionSharedMetricStateService.setMetric(this.$scope.metric);

    this.$scope.$evalAsync();

    const onError = (err: HttpErrorResponse): void => {
      this.$scope.suggestionsStatus.tasks = {
        isError: true,
        errorData: getPiErrorData("tasks", err),
      };
      this.$scope.$evalAsync();
    };

    this.getMetricAndParentGoalExistingTasksData().then(
      (existingTasks) => {
        const combinedGoalAndMetricTasks = [...existingTasks.goalTasks, ...existingTasks.metricTasks];
        const existingMetricTasksCount = existingTasks.metricTasks.length;

        const tasksToSuggest = minTasksCountNotToFetchSuggestions - existingMetricTasksCount;
        const payload = this.getQuantivePlusKrTasksSuggestionsPayload({
          metricAndParentGoalExistingTasks: combinedGoalAndMetricTasks,
          tasksToSuggest: existingMetricTasksCount >= minTasksCountNotToFetchSuggestions ? 1 : tasksToSuggest,
        });

        this.quantivePlusSuggestionsFacade
          .getQuantivePlusKrTasksSuggestion(payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (suggestions) => {
              this.$scope.suggestionsStatus.tasks = {};

              const quantivePlusTasks = suggestions.suggestions.tasks;
              this.$scope.suggestions = { sessionId: this.$scope.goal.sessionId, tasks: quantivePlusTasks, keyresults: [] };

              this.piSuggestionSharedMetricStateService.setGoal(this.$scope.goal);
              this.piSuggestionSharedMetricStateService.setMetric(this.$scope.metric);

              this.quantiveTasksFlowId = suggestions.flowId;

              this.piStateProcessorService.assigneInstanceFlowProperties(this.$scope.tasksInstance.id, {
                flowId: this.quantiveTasksFlowId,
                flowName: this.$scope.flowName,
              });

              this.piSuggestionSharedMetricStateService.setQuantiveSuggestionsFlowId(suggestions.flowId);

              this.$scope.$evalAsync();
            },
            error: (err: HttpErrorResponse) => onError(err),
          });
      },
      (err: HttpErrorResponse) => onError(err)
    );
  };

  private setSuggestionListeners = (): void => {
    this.$scope.$watchCollection("suggestions.tasks", () => {
      this.piSuggestionSharedMetricStateService.setTasks(this.$scope.suggestions);
    });
    this.$scope.$watchCollection("suggestionsStatus.tasks", (status: IQuantivePlusSuggestionStatus) => {
      this.piSuggestionSharedMetricStateService.setTasksSuggestionStatus(status);
    });

    this.piSuggestionSharedMetricStateService
      .getSingleMetricTasks$()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          if (!data.args) {
            return;
          }

          if (data.type === "refreshed") {
            this.changeQuantivePlusKrTaskSuggestion(
              data.args.task as unknown as IQuantivePlusTask,
              data.args.quantivePlusSuggestionIndex,
              PiTrackingFlowNameEnum.KeyResultDetailsView
            );
          }
        },
      });
  };

  private changeQuantivePlusKrTaskSuggestion = (taskToReplace: IQuantivePlusTask, taskToReplaceIndex: number, flowName: KeyResultFlowName): void => {
    const dummyTaskSuggestionToShowAsLoading: IQuantivePlusTask = generateDummyTaskSuggestion({ isLoading: true });
    this.$scope.suggestions.tasks[taskToReplaceIndex] = dummyTaskSuggestionToShowAsLoading;

    if (!taskToReplace.isError) {
      this.sendQuantivePlusTracking(PiTrackingEventsEnum.PiSuggestionRefreshed, flowName);
    }

    this.$scope.$evalAsync();

    const onError = (err: HttpErrorResponse): void => {
      const dummyTaskSuggestionToShowAsError: IQuantivePlusTask = generateDummyTaskSuggestion({
        isError: true,
        errorData: getPiErrorData("task", err),
      });

      this.$scope.suggestions.tasks[taskToReplaceIndex] = dummyTaskSuggestionToShowAsError;
      this.$scope.$evalAsync();
    };

    this.getMetricAndParentGoalExistingTasksData().then(
      (existingTasks) => {
        const combinedGoalAndMetricTasks = [...existingTasks.goalTasks, ...existingTasks.metricTasks];
        const tasksToSuggest = 1;

        const payload = this.getQuantivePlusKrTasksSuggestionsPayload({
          metricAndParentGoalExistingTasks: combinedGoalAndMetricTasks,
          tasksToSuggest: tasksToSuggest,
          ...(!taskToReplace.isError && {
            isRefresh: true,
          }),
          ...(!taskToReplace.isError && {
            rejectedTasks: [taskToReplace],
          }),
        });

        this.quantivePlusSuggestionsFacade
          .getQuantivePlusKrTasksSuggestion(payload)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (suggestions) => {
              const taskSuggestion = suggestions.suggestions.tasks[0];
              this.$scope.suggestions.tasks[taskToReplaceIndex] = taskSuggestion;

              this.$scope.$evalAsync();

              this.quantiveTasksFlowId = suggestions.flowId;

              this.piStateProcessorService.assigneInstanceFlowProperties(this.$scope.tasksInstance.id, {
                flowId: this.quantiveTasksFlowId,
                flowName: this.$scope.flowName,
              });

              this.piSuggestionSharedMetricStateService.setQuantiveSuggestionsFlowId(suggestions.flowId);
            },
            error: (err: HttpErrorResponse) => onError(err),
          });
      },
      (err: HttpErrorResponse) => onError(err)
    );
  };

  private getMetricAndParentGoalExistingTasksData = (): IPromise<{ goalTasks: Task[]; metricTasks: Task[] }> => {
    const promises = [
      this.taskService.getTasksV2({
        filter: { $and: [{ parentId: this.$scope.goal.id }, { $or: [{ archived: { $exists: false } }, { archived: false }] }] },
        sort: "-dueDate,dateCreated",
      }),
      this.taskService.getTasksV2({
        filter: { $and: [{ parentId: this.$scope.metric.id }, { $or: [{ archived: { $exists: false } }, { archived: false }] }] },
        sort: "-dueDate,dateCreated",
      }),
    ];

    return this.$q.all(promises).then(([goalTasks, metricTasks]: [ICollection<Task>, ICollection<Task>]) => ({
      goalTasks: goalTasks.items,
      metricTasks: metricTasks.items,
    }));
  };

  private getQuantivePlusKrTasksSuggestionsPayload = (args: {
    metricAndParentGoalExistingTasks: Task[];
    tasksToSuggest: number;
    isRefresh?: boolean;
    rejectedTasks?: IQuantivePlusTask[];
  }): PIKrTasksSuggestionPayload => {
    return generateQuantivePlusKrTasksSuggestionsPayload({
      subEntityType: "tasks",
      entityId: this.$scope.metric.id,
      metric: this.$scope.metric,
      goal: this.$scope.goal,
      assigneesMap: this.$scope.assigneesIdMap,
      teams: this.$scope.teams,
      combinedGoalAndMetricExistingTasks: args.metricAndParentGoalExistingTasks,
      tasksToSuggest: args.tasksToSuggest,
      ...(args.isRefresh && {
        isRefresh: true,
      }),
      ...(args.rejectedTasks && {
        rejectedTasks: args.rejectedTasks,
      }),
    });
  };

  private getMetric = (metricId: string): IPromise<Metric> => {
    // to do: add field projection with https://gtmhub.atlassian.net/browse/GVS-10052
    if (metricId) {
      const filter = { _id: metricId };
      let fields =
        "customFields,sourceMetricId,dueDate,obfuscated,description,assignee,url,private,settings,manualType,actual,trend,reminderSchedule,critical,dateCreated,goalOwnerId,insightName,targetOperator,goalDescription,name,fieldName,format,confidence,dynamic,access,id,goalClosedStatus,goalAttainment,assignees,goalOwnerIds,accountId,target,orderId,goalId,lastCheckInDate,attainment,numberOfOwners,ownerId,links,tasksCount,goalName,metricHistory,softDueDate,createdById,cascadeType,sessionId,ownerIds,initialValue,progressStatus,currentUserAllowedActions";
      if (this.accountService.getAccountSetting("milestones") && this.$scope.isMilestoneFeatureAvailable && this.$rootScope.featureAvailable("okrs.kr-milestones")) {
        fields += ",milestones";
      }
      return this.metricService.getMetricsV2({ filter, fields }).then((collection) => {
        if (collection.items.length) {
          return collection.items[0];
        } else {
          return this.$q.reject(customNotFoundError());
        }
      });
    }
  };

  private refreshMetric = () => {
    this.$scope.indicators.refreshingMetric = { progress: true };
    this.$scope.indicators.doNotDisplaySnapshots = { progress: true };

    this.getMetric(this.$stateParams.metricId).then(
      (metric) => {
        delete this.$scope.indicators.refreshingMetric;

        this.processMetric(metric);
        this.processContributingKRs(metric);
      },
      (error) => (this.$scope.indicators.refreshingMetric = { error })
    );
  };

  private refresh = () => {
    this.$scope.indicators.loadingUpdate = { progress: true };
    this.load();
  };

  private getGoal = (goalId: string): IPromise<unknown> => {
    const goalFields = "dateFrom,dateTo,id,name,ownerId,sessionId,locked,private,closedStatus,ownerIds,description,workflow";

    return this.goalService.getGoalWithFields(goalId, goalFields).then((goal) => {
      this.$scope.goal = goal;

      if (goal.sessionId) {
        this.getSession(goal.sessionId);
      }

      this.piFeatureVisibilityFacade
        .isSuggestingTasksToOKRsVisible$()
        .pipe(take(1))
        .subscribe((enabled) => {
          this.$scope.isSuggestingTasksToOKRsEnabled = enabled;
          if (this.$scope.isSuggestingTasksToOKRsEnabled && this.shouldFetchQuantivePlusKrTasksSuggestions()) {
            this.getQuantivePlusKrTasksSuggestions();
            this.setSuggestAnotherKrTaskSubscription();
            this.setRefreshAllKrTasksSubscription();
          }
        });

      return this.setMetricMethodology();
    });
  };

  private sendQuantivePlusTracking = (
    eventType: PiTrackingEventsEnum,
    flowName:
      | PiTrackingFlowNameEnum.KeyResultDetailsView
      | PiTrackingFlowNameEnum.KeyResultDetailsViewAllSuggestionsSidePanel = PiTrackingFlowNameEnum.KeyResultDetailsView
  ): void => {
    this.analyticsService.track(eventType, {
      flowName,
      suggestedEntity: "tasks",
      flowId: this.quantiveTasksFlowId,
      userId: getCurrentUserId(),
    });
  };

  private setSuggestAnotherKrTaskSubscription = (): void => {
    this.quantivePlusAnotherSuggestionService
      .getKrTaskSuggestionToReplace$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (
          args: {
            taskToReplace: IQuantivePlusTask;
            taskToReplaceIndex: number;
            flowName: KeyResultFlowName;
          } | null
        ) => {
          if (args) {
            this.changeQuantivePlusKrTaskSuggestion(args.taskToReplace, args.taskToReplaceIndex, args.flowName);
          }
        }
      );

    this.quantivePlusAnotherSuggestionService.setKrTaskSuggestionToReplace(null);
  };

  private setRefreshAllKrTasksSubscription = (): void => {
    this.quantivePlusAnotherSuggestionService
      .getRefreshAllKrTasksEvent$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((args: { isRefreshAllTasks: true } | null) => {
        if (args) {
          this.getQuantivePlusKrTasksSuggestions();
        }
      });

    this.quantivePlusAnotherSuggestionService.setRefreshAllKrTasksEvent(null);
    this.$scope.$evalAsync();
  };

  private setMetricMethodology(): void {
    this.$scope.methodologySettings = this.okrMethodologyFacade.getMethodologySettingsUIForMetricView(this.$scope.planningSession);
    this.setPeopleSelectorRequest();
  }

  private getSession = (id: string): void => {
    this.$scope.planningSession = this.$scope.sessionIdMap[id];
    this.measureDueDateDifference(this.$scope.metric);
    this.initAllowedActions();
  };

  // function which counts the same appearances in an object
  private countIds = (arr: (IComment | IAggregatedReaction)[]): INumericMap => {
    return arr.reduce((snapshotIdCommentsCountMap: INumericMap, currentComment: IComment): INumericMap => {
      const targetId = currentComment.targetId;

      // we increment the counter if it appear
      snapshotIdCommentsCountMap[targetId] = Object.prototype.hasOwnProperty.call(snapshotIdCommentsCountMap, targetId) ? snapshotIdCommentsCountMap[targetId] + 1 : 1;

      // at the end we have object with the counted appearanes exampale {something: 3, else: 2}
      return snapshotIdCommentsCountMap;
    }, {} as INumericMap);
  };

  private generateCalls = (metrics: string[], targetType: string, index = 0): { [index: string]: IPromise<ICollection<IAggregatedReaction | IComment>> } => {
    const promises: { [index: string]: IPromise<ICollection<IAggregatedReaction | IComment>> } = {};
    const constraints = 67;
    // url characters constraints are 2083, so we check how many targetIds can be passed at once
    // the endConstraint is 67 because the part of the url that is not ids is 80 characters and each targetId is 25
    let startConstraints = 0;
    let endConstraints: number = constraints;

    // how many times we have to make a call, because of the url length constraints
    const length: number = Math.ceil(metrics.length / endConstraints);

    for (let i = 0; i < length; i++) {
      // segmentIds are only the allowed ids for the url length condition
      const segmentIds = metrics.slice(startConstraints, endConstraints);

      if (targetType === this.strings.comments) {
        promises[i + index] = this.commentService.getComments({
          targetType: "checkin",
          targetIds: segmentIds.join(","),
        });
      } else if (targetType === this.strings.reactions) {
        promises[i + index] = this.reactionService.getReactions({ targetIds: segmentIds.join(",") });
      }

      // update the constraints conditions
      startConstraints = endConstraints;
      endConstraints += constraints;
    }

    return promises;
  };

  private processContributingKRs = (metric: Metric): void => {
    if (!metric.cascadeType) {
      return;
    }

    const totalAmount: Metric = angular.copy(metric);
    const difference: Metric = angular.copy(metric);
    totalAmount.target = metric.initialValue || 0;

    if (metric.links && metric.links.spec.cascading && metric.links.expanded) {
      const expandedMetrics = metric.links.expanded.metrics.reduce((result, expandedMetric) => {
        result[expandedMetric.id] = expandedMetric;
        return result;
      }, {});

      totalAmount.target = metric.links.spec.cascading.reduce((result, linkSpec) => {
        const linkedMetric = expandedMetrics[linkSpec.id];
        if (linkedMetric) {
          result += linkedMetric.target;
        }
        return result;
      }, totalAmount.target);
    }

    difference.target = Math.abs(metric.target - totalAmount.target);
    const isCovered = metric.target <= totalAmount.target;

    this.$scope.contributingKRsTarget = {
      isCovered,
      totalAmount,
      difference,
    };
  };

  private processMetric = (metric: Metric): IPromise<unknown> => {
    const metricsIds: string[] = [];

    const metricSnapshotMap: { [id: string]: IMetricSnapshot } = {};
    this.$scope.reactions = {};

    if (metric.metricHistory && metric.metricHistory.length > 0) {
      for (let i = 0; i < metric.metricHistory.length; i++) {
        if (dayjs(metric.metricHistory[i].updatedAt).format("YYYY-MM-DD") !== dayjs(metric.metricHistory[i].createdAt).format("YYYY-MM-DD")) {
          metric.metricHistory[i].updatedLate = true;
        }

        // array with all comments ids
        // some older comments don't have targetId, that's why we make this check
        if (metric.metricHistory[i].id !== undefined && metric.metricHistory[i].id !== "") {
          metricsIds.push(metric.metricHistory[i].id);
          metricSnapshotMap[metric.metricHistory[i].id] = metric.metricHistory[i];
          this.$scope.reactions[metric.metricHistory[i].id] = [];
        }

        metric.metricHistory[i].view = {
          color: null,
        };

        metric.metricHistory[i].view.color = this.progressColoring.getColorOrGeneric(metric.metricHistory[i].currentAttainment);
      }

      const commentsPromise = this.generateCalls(metricsIds, this.strings.comments);
      const commentsPromisesLength = Object.keys(commentsPromise).length;

      const reactionsPromise = this.generateCalls(metricsIds, this.strings.reactions, commentsPromisesLength);

      const promiseMap = {
        ...commentsPromise,
        ...reactionsPromise,
      };
      return this.$q.all<{ [id: string]: ICollection<IComment | IAggregatedReaction> }>(promiseMap).then(
        (resolvedMap) => {
          let countByTargetId: INumericMap;

          for (let i = 0; i < commentsPromisesLength; i++) {
            if (Object.prototype.hasOwnProperty.call(resolvedMap, i)) {
              // group comments by targetId(their parent comment id)
              countByTargetId = this.countIds(resolvedMap[i].items);

              // setting the count of the comments which is used in the view
              for (const key in metricSnapshotMap) {
                if (Object.prototype.hasOwnProperty.call(metricSnapshotMap, key)) {
                  if (countByTargetId[key]) {
                    metricSnapshotMap[key].commentsCount = countByTargetId[key];
                  } else if (!countByTargetId[key] && !metricSnapshotMap[key].commentsCount) {
                    metricSnapshotMap[key].commentsCount = 0;
                  }
                }
              }
            }
          }

          const resolvedMapLength = Object.keys(resolvedMap).length;

          for (let i = commentsPromisesLength; i < resolvedMapLength; i++) {
            resolvedMap[i].items.forEach((reaction: IAggregatedReaction) => {
              if (!this.$scope.reactions[reaction.targetId]) {
                this.$scope.reactions[reaction.targetId] = [];
              }

              this.$scope.reactions[reaction.targetId].push(reaction);
            });
          }

          this.$scope.metric = metric;

          this.$timeout(() => {
            delete this.$scope.indicators.doNotDisplaySnapshots;
          }, 50);
        },
        (error) => {
          this.$scope.indicators.doNotDisplaySnapshots = { error };
        }
      );
    } else {
      delete this.$scope.indicators.doNotDisplaySnapshots;
      this.$scope.metric = metric;
    }
  };

  private getSnapshotSortField = (snapshot: IMetricSnapshot): number => {
    return dayjs(snapshot.updatedAt).valueOf();
  };

  private redirectToGoal = (sessionId: string, goalId: string) => {
    if (this.$rootScope.isMsTeamsState) {
      return;
    }

    const params = {
      planningSessionId: sessionId,
      id: goalId,
    };

    const statesMap = {
      ".all.": { name: "gtmhub.goals.all._goalSidebar.goal", params },
      ".alignment.": { name: "gtmhub.goals.alignment._goalSidebar.goal", params },
      "alignmentSidebar.tree": { name: "gtmhub.goals.alignmentSidebar.tree._goalSidebar.goal", params },
      ".home.feed": { name: "gtmhub.home.feed._goals._goalSidebar.goal", params },
      ".home.dashboard": { name: "gtmhub.home.dashboard._goals._goalSidebar.goal", params },
      ".msTeamsHome.dashboard": { name: "gtmhub.msTeamsHome.dashboard._goals._goalSidebar.goal", params },
      ".msTeamsHome.feed": { name: "gtmhub.msTeamsHome.feed._goals._goalSidebar.goal", params },
      ".insightboard.": { name: "gtmhub.insightboard._goalSidebar.goal", params: { ...params, returnState: "gtmhub.insightboard" } },
    };

    for (const key of Object.keys(statesMap)) {
      if (this.isIncludesInStateName(key)) {
        this.$state.go(statesMap[key].name, statesMap[key].params);
        return;
      }
    }

    this.$state.go("gtmhub.goals.all._goalSidebar.goal", params);
  };

  private isIncludesInStateName = (stringToCheck: string): boolean => {
    return this.$state.current.name.includes(stringToCheck);
  };

  private onReactionCreated = (event: IAngularEvent, newReaction: IReaction): void => {
    if (this.$scope.reactions[newReaction.targetId]) {
      this.$scope.reactions[newReaction.targetId] = this.reactionService.onReactionCreated(this.$scope.reactions[newReaction.targetId], newReaction);
    }
  };

  private onReactionDeleted = (event: IAngularEvent, deletedReaction: IAggregatedReaction): void => {
    if (this.$scope.reactions[deletedReaction.targetId]) {
      this.$scope.reactions[deletedReaction.targetId] = this.reactionService.onReactionDeleted(this.$scope.reactions[deletedReaction.targetId], deletedReaction);
    }
  };

  private openDisableSyncModal = (metricId: string): void => {
    this.disableMetricSync.open(metricId);
  };

  private getSourceMetric = (metricId: string): void => {
    if (metricId) {
      const filter = { _id: metricId };
      const fields = "id,insightName";

      this.metricService
        .getMetricsV2({ filter, fields })
        .then((collection) => {
          // it should always be one element
          this.$scope.sourceMetric = collection.items[0] || ({} as Metric);
        })
        .catch((err) => this.uiErrorHandlingService.handleModal(err));
    }
  };

  private copyItemUrlToClipboard = (): void => {
    const copyToClipboardOptions = { container: getCopyURLNotificationEl(), duration: 3000 };
    const url = this.getShareableUrl();

    copyToClipboard(url, copyToClipboardOptions);
  };

  private getShareableUrl = (): string => {
    const states = {
      alignmentTree: "gtmhub.goals.alignmentSidebar.tree",
      grid: "gtmhub.goals.all._metric.metric",
    };

    if (this.isIncludesInStateName(states.alignmentTree)) {
      return this.$state.href(
        states.alignmentTree,
        { ...this.$stateParams, sharedId: this.$scope.metric.id, sharedParentId: this.$scope.metric.goalId },
        { absolute: true }
      );
    }

    const isHomeOrList = this.isIncludesInStateName(".home.") || this.isIncludesInStateName(".msTeamsHome.") || this.isIncludesInStateName("gtmhub.list");
    if (isHomeOrList) {
      return this.$state.href(
        states.grid,
        {
          metricId: this.$scope.metric.id,
          planningSessionId: this.$scope.metric.sessionId,
          sharedMetric: true,
        },
        { absolute: true }
      );
    }

    return this.$state.href(this.$state.current, { ...this.$stateParams, sharedMetric: true }, { absolute: true });
  };

  private getContributingMetricsNames = (metric: Metric): IPromise<unknown> => {
    const cascadingMetricsIds = metric.links?.spec?.cascading?.map((cascading) => cascading.id) || [];
    if (cascadingMetricsIds.length) {
      return this.metricService
        .getMetricsV2({
          filter: { _id: { $in: cascadingMetricsIds } },
          fields: "name,goalId",
        })
        .then((metrics) => {
          this.$scope.contributingMetrics = metrics.items;
        });
    }
  };

  private checkSuggestionStateIndicatorFeatureToggle = (): IPromise<void> => {
    const overviewDefer = this.$q.defer<void>();

    this.piFeatureVisibilityFacade
      .isAtLeastOneTabVisible$()
      .pipe(take(1))
      .subscribe({
        next: (enabled) => {
          this.$scope.isSuggestionStateIndicatorEnabled = enabled;
          overviewDefer.resolve();
        },
        error: () => {
          overviewDefer.resolve();
        },
      });

    return overviewDefer.promise;
  };

  private checkSuggestionDrawerOverviewTabFeatureToggle = (): IPromise<void> => {
    const overviewDefer = this.$q.defer<void>();

    this.piFeatureVisibilityFacade
      .isOverviewVisible$()
      .pipe(take(1))
      .subscribe({
        next: (enabled) => {
          this.$scope.isSuggestionDrawerOverviewTabEnabled = enabled;
          overviewDefer.resolve();
        },
        error: () => {
          overviewDefer.resolve();
        },
      });

    return overviewDefer.promise;
  };

  private checkSuggestionDrawerEffectivenessTabFeatureToggle = (): IPromise<void> => {
    const overviewDefer = this.$q.defer<void>();

    this.piFeatureVisibilityFacade
      .isEffectivenessVisible$()
      .pipe(take(1))
      .subscribe({
        next: (enabled) => {
          this.$scope.isSuggestionDrawerEffectivenessTabEnabled = enabled;
          overviewDefer.resolve();
        },
        error: () => {
          overviewDefer.resolve();
        },
      });

    return overviewDefer.promise;
  };

  private changeSuggestionDrawerVisibility = (isVisible: boolean): void => {
    this.$scope.shouldDisplaySuggestionDrawer = isVisible;
    this.$scope.$evalAsync();

    if (!this.$scope.drawerExplanationSection.explanation) {
      this.loadSuggestionDrawerExplanation({ ...this.$scope.metric });
    }
  };

  private filterBySessionId = (customFields: ICustomField[]): ICustomField[] => {
    return filterCustomFieldsBySession(this.$scope.metric.sessionId, customFields);
  };

  private openMetricInAlignmentView = ($event) => {
    if (!$event || $event.keyCode === "13") {
      this.$state.go(
        "gtmhub.goals.alignmentSidebar.tree",
        { planningSessionId: this.$scope.metric.sessionId, locateId: this.$scope.metric.goalId },
        { location: "replace" }
      );
    }
  };

  private isCustomFieldDisabled = (customFieldName: string): boolean => {
    return (
      !this.$scope.allowEdit ||
      this.isCustomFieldPartOfApprovedGoalWorkflow(this.$scope.goal?.workflow?.status, this.$scope.planningSession?.workflow?.editableKrFields, customFieldName)
    );
  };

  private isCustomFieldPartOfApprovedGoalWorkflow(goalStatus: WorkflowStatus, editableKrFields: string[], customFieldName: string): boolean {
    if (goalStatus === "approved") {
      // field is not part of the editable fields
      return !editableKrFields?.includes(customFieldName);
    }

    return false;
  }

  private loadSuggestionDrawerExplanation(metricData: { name?: string; description?: string; ownerIds?: string[] }): void {
    if (!this.$scope.isSuggestionDrawerOverviewTabEnabled) return;

    this.$scope.drawerExplanationSection = {
      ...this.$scope.drawerExplanationSection,
      isLoading: true,
      hasError: false,
    };

    this.$scope.$evalAsync();

    const payload = this.generateQuantivePlusKeyResultsExplanationPayload({ ...metricData, entityId: this.$scope.metric.id, subEntityType: "overview" });

    this.quantivePlusExplanationFacade
      .getQuantivePlusKeyResultsExplanation(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (explanationRes) => {
          this.$scope.drawerExplanationSection = {
            ...this.$scope.drawerExplanationSection,
            explanation: explanationRes.suggestions[0].explanation[0],
            flowId: explanationRes.flowId,
            hasError: false,
            isLoading: false,
          };
          this.$scope.$evalAsync();
        },
        error: (err: HttpErrorResponse) => {
          this.$scope.drawerExplanationSection = {
            ...this.$scope.drawerExplanationSection,
            isLoading: false,
            hasError: true,
            errorData: getPiErrorData("explanation", err),
          };
          this.$scope.$evalAsync();
        },
      });
  }

  private loadSuggestionDrawerEffectiveness(metricData: Pick<Metric, "name" | "description" | "ownerIds" | "target">): void {
    if (!this.$scope.isSuggestionDrawerEffectivenessTabEnabled) return;

    this.$scope.drawerEffectivenessSection = {
      ...this.$scope.drawerEffectivenessSection,
      isLoading: true,
      hasError: false,
    };

    this.$scope.$evalAsync();

    const payload = this.generateQuantivePlusKeyResultsEffectivenessPayload({
      ...metricData,
      refresh: false,
      accepted: {},
      rejected: {},
      entityId: this.$scope.metric.id,
      subEntityType: "effectiveness",
    });

    this.quantivePlusEffectivenessFacade
      .getQuantivePlusKeyResultsEffectiveness(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (effectivenessRes) => {
          this.$scope.effectivenessFlowId = effectivenessRes.flowId;

          this.$scope.drawerEffectivenessSection = {
            ...this.$scope.drawerEffectivenessSection,
            effectiveness: effectivenessRes.suggestions[0].effectiveness[0],
            hasError: false,
            isLoading: false,
          };

          const suggestionText = effectivenessRes.suggestions[0]?.effectiveness[0]?.suggested_keyresult_title;
          const suggestionId = effectivenessRes.suggestions[0]?.effectiveness[0]?.id;

          this.$scope.drawerTitleSection = {
            ...this.$scope.drawerTitleSection,
            isLoading: false,
            hasError: false,
            isHidden: !suggestionText || (!this.$scope.allowEdit && !this.$scope.isFieldPartOfWorkflow("name")),
            suggestions: [
              {
                id: suggestionId,
                suggestionText,
              },
            ],
          };

          this.$scope.$evalAsync();
        },
        error: (err: HttpErrorResponse) => {
          this.$scope.drawerEffectivenessSection = {
            ...this.$scope.drawerEffectivenessSection,
            isLoading: false,
            hasError: true,
            errorData: getPiErrorData("effectiveness", err),
          };
          this.$scope.drawerTitleSection = {
            ...this.$scope.drawerTitleSection,
            isLoading: false,
            hasError: true,
            errorData: getPiErrorData("title", err),
          };

          this.$scope.$evalAsync();
        },
      });
  }

  private loadTitleSuggestionPartOfEffectiveness(
    metricData: Pick<Metric, "name" | "description" | "ownerIds"> & { isRefresh: boolean; rejected: SuggestedEffectivenessTitle }
  ): void {
    if (!this.$scope.isSuggestionDrawerEffectivenessTabEnabled) return;

    this.$scope.drawerTitleSection = {
      ...this.$scope.drawerTitleSection,
      isLoading: true,
      hasError: false,
    };
    this.$scope.$evalAsync();
    const payload = this.generateQuantivePlusKeyResultsEffectivenessPayload({
      ...metricData,
      refresh: metricData.isRefresh,
      accepted: {},
      rejected: metricData.rejected,
      entityId: this.$scope.metric.id,
      subEntityType: "effectiveness",
    });

    this.quantivePlusEffectivenessFacade
      .getQuantivePlusKeyResultsEffectiveness(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (effectivenessRes) => {
          const suggestionText = effectivenessRes.suggestions[0]?.effectiveness[0]?.suggested_keyresult_title;
          this.$scope.drawerTitleSection = {
            ...this.$scope.drawerTitleSection,
            isLoading: false,
            hasError: false,
            isHidden: !suggestionText || (!this.$scope.allowEdit && !this.$scope.isFieldPartOfWorkflow("name")),
            suggestions: [{ id: uuidv4(), suggestionText }],
          };

          this.$scope.$evalAsync();
        },
        error: (err: HttpErrorResponse) => {
          this.$scope.drawerTitleSection = {
            ...this.$scope.drawerTitleSection,
            isLoading: false,
            hasError: true,
            errorData: getPiErrorData("title", err),
          };
          this.$scope.$evalAsync();
        },
      });
  }

  private setAcceptedTitleSuggestion = (): void => {
    this.$scope.metric.name = this.$scope.drawerTitleSection.suggestions[0].suggestionText;
    this.updateName();
  };

  private showLastAvailableTitleSuggestion = (): void => {
    this.$scope.drawerTitleSection = {
      ...this.$scope.drawerTitleSection,
      hasError: false,
      errorData: getPiErrorData("title", null),
    };

    this.$scope.$evalAsync();
  };

  private generateQuantivePlusKeyResultsExplanationPayload = ({
    name,
    description,
    ownerIds,
    entityId,
    subEntityType,
  }: {
    name?: string;
    description?: string;
    ownerIds?: string[];
    entityId: string;
    subEntityType: PIStateProcessorInstanceSubType;
  }): QuantivePlusKeyResultsExplanationPayload => {
    const metricAssignees = generateAssignees(ownerIds, this.$scope.assigneesIdMap, this.$scope.teams);

    return {
      entityId,
      subEntityType,
      refresh: false,
      keyresults: [
        {
          krTitle: name,
          krDescription: description,
          initialValue: this.$scope.metric.initialValue,
          targetValue: this.$scope.metric.target,
          actualValue: this.$scope.metric.actual,
          parentTitle: this.$scope.goal.name,
          parentDescription: this.$scope.goal.description,
          assignees: metricAssignees,
          accepted: {},
          rejected: {},
        },
      ],
    };
  };

  private generateQuantivePlusKeyResultsEffectivenessPayload = ({
    name,
    description,
    ownerIds,
    target,
    refresh,
    accepted,
    rejected,
    entityId,
    subEntityType,
  }: {
    name?: string;
    description?: string;
    ownerIds?: string[];
    target?: number;
    refresh: boolean;
    accepted: SuggestedEffectivenessTitle;
    rejected: SuggestedEffectivenessTitle;
    entityId: string;
    subEntityType: PIStateProcessorInstanceSubType;
  }): QuantivePlusKeyResultsEffectivenessPayload => {
    return generateQuantivePlusKeyResultsEffectivenessPayload({
      entityId,
      subEntityType,
      name,
      description,
      ownerIds,
      target,
      initialValue: this.$scope.metric.initialValue,
      actual: this.$scope.metric.actual,
      parentTitle: this.$scope.goal.name,
      parentDescription: this.$scope.goal.description,
      assigneesIdMap: this.$scope.assigneesIdMap,
      teams: this.$scope.teams,
      refresh,
      accepted,
      rejected,
    });
  };

  private reflectFieldChangeWithinSuggestionDrawer = (metricData: Pick<Metric, "name" | "description" | "ownerIds" | "target">): void => {
    if (!this.$scope.isSuggestionStateIndicatorEnabled) return;

    this.loadSuggestionDrawerEffectiveness(metricData);

    if (!this.$scope.shouldDisplaySuggestionDrawer && !this.$scope.drawerEffectivenessSection.isLoading) {
      this.$scope.drawerExplanationSection = {
        ...this.$scope.drawerExplanationSection,
        explanation: undefined,
      };
      this.reflectChangeWithinSuggestionStateIndicatorOnly();

      return;
    }

    this.loadSuggestionDrawerExplanation(metricData);
  };

  private reflectChangeWithinSuggestionStateIndicatorOnly = () => {
    this.$scope.drawerExplanationSection = {
      ...this.$scope.drawerExplanationSection,
      isLoading: true,
    };
    this.$timeout(() => {
      this.$scope.drawerExplanationSection = {
        ...this.$scope.drawerExplanationSection,
        isLoading: false,
      };
    }, 2000);
  };

  private createDropdownOptions() {
    const menuItemBuilder = new DropdownMenuItemBuilder();
    const dropdownOptions: DropdownMenuItem[] = [];

    if (this.$scope.allowEdit) {
      if (this.$scope.metric.insightName === "" && !this.isMetricOfAbandonedGoal()) {
        const editItem = menuItemBuilder
          .setKey("edit")
          .setTrack("Metric Form Menu Action Clicked")
          .setTrackMetaData({ type: "Edit", parent: this.$scope.parentState })
          .setAction({ state: "._editMetric.editMetric", params: { id: this.$scope.goal.id, metricId: this.$scope.metric.id } })
          .build();
        dropdownOptions.push(editItem);
      } else if (this.$scope.metric.insightName !== "" && !this.isMetricOfAbandonedGoal()) {
        const editItem = menuItemBuilder
          .setKey("edit")
          .setTrack("Metric Form Menu Action Clicked")
          .setTrackMetaData({ type: "Edit", parent: this.$scope.parentState })
          .setAction({ state: "._editMetric.editDynamicMetric", params: { id: this.$scope.goal.id, metricId: this.$scope.metric.id } })
          .build();
        dropdownOptions.push(editItem);
      }

      if (this.$scope.metric.daysLeft === undefined || this.$scope.metric.daysLeft > 0 || !this.isMetricOfAbandonedGoal()) {
        const moveItem = menuItemBuilder
          .setKey("move")
          .setAction({ handler: (): void => this.openMoveMetricModal(this.$scope.metric) })
          .build();
        dropdownOptions.push(moveItem);
      }
    }

    if (this.$scope.metric.daysLeft === undefined || this.$scope.metric.daysLeft > 0) {
      const cloneItem = menuItemBuilder
        .setKey("clone")
        .setTrack("Metric Form Menu Action Clicked")
        .setTrackMetaData({ type: "Clone", parent: this.$scope.parentState })
        .setAction({ handler: (): void => this.openCloneMetricModal(this.$scope.metric) })
        .build();
      dropdownOptions.push(cloneItem);
    }

    if (this.$scope.metric.sourceMetricId) {
      const disableProgressSyncItem = menuItemBuilder
        .setKey("disable_progress_sync")
        .setAction({ handler: (): void => this.openDisableSyncModal(this.$scope.metric.id) })
        .build();
      dropdownOptions.push(disableProgressSyncItem);
    }

    const copyLinkItem = menuItemBuilder
      .setKey("copy_url")
      .setTrack("Metric Form Menu Action Clicked")
      .setTrackMetaData({ type: "Copy URL", parent: this.$scope.parentState })
      .setAction({ handler: (): void => this.copyItemUrlToClipboard() })
      .build();
    dropdownOptions.push(copyLinkItem);

    if (this.$scope.allowDelete) {
      const deleteItem = menuItemBuilder
        .setKey("delete")
        .setToBeDestructive()
        .setTrack("Metric Form Menu Action Clicked")
        .setTrackMetaData({ type: "Delete", parent: this.$scope.parentState })
        .setAction({ handler: (): void => this.confirmDeleteMetric(this.$scope.metric.id, this.$scope.goal.id) })
        .build();
      dropdownOptions.push(deleteItem);
    }

    return dropdownOptions;
  }
}
